import { forwardRef, type PropsWithoutRef, useCallback } from 'react';

import type { RefElement } from '../../types';
import { useConsolidatedRef, useI18n, useFocusWithin, useTestIds, useUID } from '../../hooks';
import { StyledRangeInputContainer } from '../Input/Input.styles';
import FormField from '../FormField';
import Flex from '../Flex';
import { withTestIds, createClassName } from '../../utils';
import HiddenText from '../HiddenText';

import NumberInput from './NumberInput';
import type { NumberRangeInputProps } from './NumberInput.types';
import { getNumberRangeTestIds } from './Number.test-ids';

export default withTestIds(
  forwardRef<RefElement<NumberRangeInputProps>, PropsWithoutRef<NumberRangeInputProps>>(
    function NumberRangeInput(
      {
        name,
        testId,
        value = {},
        onChange,
        onBlur,
        onFocus,
        label,
        fromLabel,
        toLabel,
        labelHidden,
        placeholder,
        fromPlaceholder,
        toPlaceholder,
        disabled,
        readOnly,
        required,
        status,
        unit,
        showDecimal,
        numberOfDecimals,
        showGroupSeparators,
        min,
        max,
        step,
        variant,
        fromAdditionalInfo,
        toAdditionalInfo,
        autoFocus,
        handle,
        className,
        ...restProps
      },
      ref
    ) {
      const testIds = useTestIds(testId, getNumberRangeTestIds);

      const t = useI18n();

      const start = value.start ?? '';
      const end = value.end ?? '';

      const onFocusChange = useCallback(
        (focused: boolean) => {
          (focused ? onFocus : onBlur)?.({ start, end });
        },
        [onBlur, value.start, value.end]
      );

      const containerRef = useConsolidatedRef(ref);
      useFocusWithin([containerRef], onFocusChange);

      const commonProps = {
        status,
        readOnly,
        required,
        unit,
        showDecimal,
        numberOfDecimals,
        showGroupSeparators,
        min,
        max,
        variant
      };

      const descriptionId = useUID();

      return (
        <>
          <FormField
            testId={testIds}
            as='fieldset'
            {...restProps}
            ref={containerRef}
            container={{ wrap: 'nowrap' }}
            labelHidden={labelHidden}
            label={label}
            disabled={disabled}
            required={required}
            readOnly={readOnly}
            status={status}
            aria-describedby={descriptionId}
          >
            <Flex
              container
              as={StyledRangeInputContainer}
              className={createClassName('number-range-input', className)}
            >
              <NumberInput
                testId={testIds.min}
                {...commonProps}
                placeholder={fromPlaceholder ?? placeholder}
                value={start}
                onChange={fromValue => onChange({ start: fromValue, end })}
                label={fromLabel}
                aria-label={`${t('range_from_numeric') + (label ? ` - ${label}` : '')}`}
                additionalInfo={fromAdditionalInfo}
                autoFocus={autoFocus}
              />
              <NumberInput
                testId={testIds.max}
                {...commonProps}
                placeholder={toPlaceholder ?? placeholder}
                value={end}
                onChange={toValue => onChange({ start, end: toValue })}
                label={toLabel}
                aria-label={`${t('range_to_numeric') + (label ? ` - ${label}` : '')}`}
                additionalInfo={toAdditionalInfo}
              />
            </Flex>
          </FormField>
          <HiddenText id={descriptionId}>{t('range_input')}</HiddenText>
        </>
      );
    }
  ),
  getNumberRangeTestIds
);
